// app/javascript/controllers/experience_sectors_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "hiddenField"]
  static values = { 
    existingData: Object,
    useTomSelect: Boolean 
  }

  connect() {
    this.container = this.containerTarget || document.getElementById('experience-sectors-container')
    
    if (this.useTomSelectValue) {
      this.initializeTomSelectMode()
    } else {
      this.initializeManualMode()
    }
  }

  initializeTomSelectMode() {
    // Mode Tom Select - on écoute les événements du Tom Select
    this.setupTomSelectListeners()
  }

  initializeManualMode() {
    // Mode manuel - on charge les données existantes
    this.loadExistingDataManual()
  }

  setupTomSelectListeners() {
    this.element.addEventListener('tom-select:change', (event) => {
      if (event.detail.mode === 'sector-experience') {
        this.updateHiddenFieldFromTomSelect(event.detail.value)
      }
    })
  }

  updateHiddenFieldFromTomSelect(values) {
    if (!this.hasHiddenFieldTarget) return

    const experienceData = {}
    
    if (Array.isArray(values)) {
      values.forEach(value => {
        const [sector, duration] = value.split('|')
        if (sector && duration) {
          experienceData[sector] = duration
        }
      })
    }

    this.hiddenFieldTarget.value = JSON.stringify(experienceData)
    this.dispatchExperienceUpdate(experienceData)
  }

  // Méthodes pour le mode manuel (champs individuels)
  addSector(event) {
    event.preventDefault()
    
    const newSectorHtml = `
      <div class="flex items-center space-x-2 experience-sector-item">
        <input type="text" 
               name="sector_name" 
               class="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               placeholder="ex: Agriculture, Santé, Éducation..."
               data-action="blur->experience-sectors#updateExperience">
        <input type="text" 
               name="sector_duration" 
               class="w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               placeholder="2 ans"
               data-action="blur->experience-sectors#updateExperience">
        <button type="button" 
                class="text-red-500 hover:text-red-700"
                data-action="click->experience-sectors#removeSector">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
    `
    
    this.container.insertAdjacentHTML('beforeend', newSectorHtml)
    
    // Focus sur le nouveau champ secteur
    const newInput = this.container.lastElementChild.querySelector('input[name="sector_name"]')
    newInput.focus()
  }

  removeSector(event) {
    event.preventDefault()
    const sectorItem = event.target.closest('.experience-sector-item')
    
    if (sectorItem) {
      sectorItem.remove()
      this.updateExperience()
    }
  }

  updateExperience() {
    if (!this.hasHiddenFieldTarget) return

    const experienceData = {}
    const sectorItems = this.container.querySelectorAll('.experience-sector-item')
    
    sectorItems.forEach(item => {
      const sectorInput = item.querySelector('input[name="sector_name"]')
      const durationInput = item.querySelector('input[name="sector_duration"]')
      
      const sector = sectorInput?.value?.trim().toLowerCase()
      const duration = durationInput?.value?.trim()
      
      if (sector && duration) {
        experienceData[sector] = duration
      }
    })

    this.hiddenFieldTarget.value = JSON.stringify(experienceData)
    this.dispatchExperienceUpdate(experienceData)
  }

  loadExistingDataManual() {
    if (!this.hasExistingDataValue || this.useTomSelectValue) return

    const existingData = this.existingDataValue
    
    Object.entries(existingData).forEach(([sector, duration]) => {
      this.addSectorWithData(sector, duration)
    })
  }

  addSectorWithData(sector, duration) {
    const newSectorHtml = `
      <div class="flex items-center space-x-2 experience-sector-item">
        <input type="text" 
               name="sector_name" 
               value="${this.escapeHtml(sector)}"
               class="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               placeholder="ex: Agriculture, Santé, Éducation..."
               data-action="blur->experience-sectors#updateExperience">
        <input type="text" 
               name="sector_duration" 
               value="${this.escapeHtml(duration)}"
               class="w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
               placeholder="2 ans"
               data-action="blur->experience-sectors#updateExperience">
        <button type="button" 
                class="text-red-500 hover:text-red-700"
                data-action="click->experience-sectors#removeSector">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
    `
    
    this.container.insertAdjacentHTML('beforeend', newSectorHtml)
  }

  dispatchExperienceUpdate(data) {
    const event = new CustomEvent('sector-experience:updated', {
      detail: { data: data },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Méthodes publiques pour interaction avec Tom Select
  loadExistingDataForTomSelect(data) {
    if (!this.useTomSelectValue) return

    const tomSelectElement = this.element.querySelector('[data-controller*="tom-select"]')
    if (tomSelectElement) {
      const tomSelectController = this.application.getControllerForElementAndIdentifier(
        tomSelectElement, 
        'tom-select'
      )
      
      if (tomSelectController && data) {
        Object.entries(data).forEach(([sector, duration]) => {
          const value = `${sector}|${duration}`
          tomSelectController.tomSelect.addOption({
            value: value,
            sector: sector,
            duration: duration,
            display: `${sector} (${duration})`
          })
          tomSelectController.tomSelect.addItem(value, true)
        })
      }
    }
  }
}